import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Tag, Loader2, ChevronDown, Check, CheckCheck, XCircle, Loader2 as Spinner, Truck, FileText } from 'lucide-react';
import { User, Message, Tag as TagType } from '../../types';
import { searchUsersApi } from '../../api/users';
import { getTagsApi } from '../../api/tags';
import TagManagerModal from '../tags/TagManagerModal';
import TrackingModal from './TrackingModal';
import AnalysisLogModal from './AnalysisLogModal';
import ModelConfigModal from './ModelConfigModal';

interface UserListProps {
  onSelectUser: (user: User) => void;
  selectedUserId: string | null;
}

const formatTimestamp = (isoString: string) => {
  if (!isoString) return '';

  const date = new Date(isoString);
  const now = new Date();

  // 获取当地时间的日期部分（用于比较）
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // 计算天数差
  const diffTime = today.getTime() - dateOnly.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // 今天：显示时间 (14:32)
  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  }

  // 昨天：显示"昨天"
  if (diffDays === 1) {
    return '昨天';
  }

  // 本周内（2-6天前）：显示星期几
  if (diffDays >= 2 && diffDays <= 6) {
    const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    return weekdays[date.getDay()];
  }

  // 更早：显示日期 (12/15)
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

const MessageStatus: React.FC<{ status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed' }> = ({ status }) => {
  switch (status) {
    case 'sending':
      return <Spinner className="w-4 h-4 animate-spin text-muted-foreground" />;
    case 'sent':
      return <Check className="w-4 h-4 text-muted-foreground" />;
    case 'delivered':
      return <CheckCheck className="w-4 h-4 text-muted-foreground" />;
    case 'read':
      return <CheckCheck className="w-4 h-4 text-whatsapp-blue" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-destructive" />;
    default:
      return null;
  }
};

const getLastMessagePreview = (message?: Message) => {
  if (!message) return '暂无消息';
  switch (message.type) {
    case 'image': return '📷 图片';
    case 'video': return '📹 视频';
    case 'audio': return '🎵 音频';
    case 'document': return '📄 文件';
    default: return message.content;
  }
}

interface UserItemProps {
  user: User & { lastMessage?: Message, tagsDetails?: TagType[] };
  isSelected: boolean;
  onSelect: () => void;
  searchHighlights?: { field: string; snippet: string }[];
}

const UserItem: React.FC<UserItemProps> = ({ user, isSelected, onSelect, searchHighlights }) => {
  const lastMessageContent = getLastMessagePreview(user.lastMessage);
  const lastMessageTimestamp = user.lastMessage ? formatTimestamp(user.lastMessage.timestamp) : '';

  return (
    <li
      onClick={onSelect}
      className={`flex items-start p-3 space-x-3 cursor-pointer transition-colors duration-200 border-l-4 ${isSelected
        ? 'bg-secondary border-primary'
        : 'border-transparent hover:bg-muted'
        }`}
    >
      <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 min-w-0">
            <p className="font-semibold truncate">{user.name}</p>
            <div className="flex gap-1 flex-shrink-0">
              {user.tagsDetails?.map(tag => (
                <div
                  key={tag.id}
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: tag.color }}
                  title={tag.name}
                ></div>
              ))}
            </div>
          </div>
          <span className="text-xs text-muted-foreground flex-shrink-0">{lastMessageTimestamp}</span>
        </div>
        <div className="flex justify-between items-start mt-1">
          <div className="text-sm text-muted-foreground truncate flex-1">
            {Array.isArray((user as any).highlights) && (user as any).highlights.length > 0 ? (
              <div className="space-y-1">
                {(user as any).highlights.slice(0, 3).map((snippet: string, index: number) => (
                  <p key={index} className="text-xs" dangerouslySetInnerHTML={{ __html: snippet }} />
                ))}
              </div>
            ) : (
              lastMessageContent
            )}
          </div>
          <div className="ml-2 flex-shrink-0 flex flex-col items-end gap-1">
            {user.lastMessage?.sender === 'me' && (
              <MessageStatus status={user.lastMessage.status} />
            )}
            {user.unreadCount && user.unreadCount > 0 ? (
              <span className="flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-whatsapp-green rounded-full">
                {user.unreadCount > 99 ? '99+' : user.unreadCount}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
};

const TagFilterDropdown: React.FC<{
  tags: TagType[];
  activeTags: string[];
  toggleTag: (tagId: string) => void;
}> = ({ tags, activeTags, toggleTag }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between px-3 py-2 bg-input border border-border rounded-md text-sm hover:bg-muted">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-muted-foreground" />
          <span>标签筛选</span>
          {activeTags.length > 0 && <span className="bg-primary text-primary-foreground text-xs rounded-full px-2">{activeTags.length}</span>}
        </div>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full mt-2 w-64 bg-popover border border-border rounded-lg shadow-lg z-20">
          <div className="p-2 font-semibold text-sm border-b border-border">按标签筛选</div>
          <div className="p-2 space-y-2">
            {tags.map(tag => (
              <label key={tag.id} className="flex items-center space-x-2 cursor-pointer p-1 rounded hover:bg-muted">
                <input
                  type="checkbox"
                  checked={activeTags.includes(tag.id)}
                  onChange={() => toggleTag(tag.id)}
                  className="form-checkbox h-4 w-4 rounded text-primary bg-input border-border focus:ring-primary"
                  style={{ accentColor: tag.color }}
                />
                <span className="px-2 py-0.5 text-xs rounded-full" style={{ backgroundColor: tag.color, color: 'hsl(0 0% 100%)' }}>
                  {tag.name}
                </span>
              </label>
            ))}
          </div>
          {activeTags.length > 0 && (
            <div className="p-2 border-t border-border">
              <button onClick={() => activeTags.forEach(tagId => toggleTag(tagId))} className="text-xs text-muted-foreground hover:text-destructive w-full text-center">
                清除所有
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const UserList: React.FC<UserListProps> = ({ onSelectUser, selectedUserId }) => {
  const [displayedUsers, setDisplayedUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [activeTags, setActiveTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<TagType[]>([]);
  const [isTagManagerOpen, setIsTagManagerOpen] = useState(false);
  const [isTrackingOpen, setIsTrackingOpen] = useState(false);
  const [isAnalysisLogOpen, setIsAnalysisLogOpen] = useState(false);
  const [isModelConfigOpen, setIsModelConfigOpen] = useState(false);
  const statusSyncRef = useRef<number>(0);
  const backupSyncRef = useRef<NodeJS.Timeout | undefined>();
  const listContainerRef = useRef<HTMLDivElement>(null);
  const isSearchActiveRef = useRef(false);
  const allTagsRef = useRef<TagType[]>([]);
  const searchTermRef = useRef('');
  const activeTagsRef = useRef<string[]>([]);
  const lastWsMessageTimeRef = useRef<number>(0);
  const PAGE_SIZE = 20;

  // 同步状态到 ref，避免闭包过期问题
  useEffect(() => {
    isSearchActiveRef.current = !!(searchTerm || activeTags.length > 0);
    searchTermRef.current = searchTerm;
    activeTagsRef.current = activeTags;
  }, [searchTerm, activeTags]);

  // 同步 allTags 到 ref
  useEffect(() => {
    allTagsRef.current = allTags;
  }, [allTags]);


  useEffect(() => {
    const handleWsConnected = () => {
      // 连接恢复后，仅在无活跃搜索/筛选时同步列表
      if (!isSearchActiveRef.current) {
        performSearch();
      }
    };
    const fetchInitialData = async () => {
      try {
        const tags = await getTagsApi();
        setAllTags(tags);
      } catch (error) {
        console.error("Failed to fetch tags:", error);
      }
    };
    fetchInitialData();
  }, [isTagManagerOpen]); // Refresh tags when manager closes

  // Listen for chat cleared event
  useEffect(() => {
    const handleChatCleared = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { userId } = customEvent.detail;
      setDisplayedUsers(prevUsers =>
        prevUsers.map(user => {
          if (user.id === userId) {
            // Reset last message to undefined or a placeholder, and reset unread count
            return { ...user, lastMessage: undefined, unreadCount: 0 };
          }
          return user;
        })
      );
    };

    const handleChatOpened = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { userId } = customEvent.detail;
      setDisplayedUsers(prevUsers =>
        prevUsers.map(user => {
          if (user.id === userId) {
            return { ...user, unreadCount: 0 };
          }
          return user;
        })
      );
    };

    window.addEventListener('chat_cleared', handleChatCleared);
    window.addEventListener('chat_opened', handleChatOpened);
    return () => {
      window.removeEventListener('chat_cleared', handleChatCleared);
      window.removeEventListener('chat_opened', handleChatOpened);
    };
  }, [allTags, activeTags, searchTerm]);

  // 统一使用全局事件总线更新用户列表
  useEffect(() => {
    const handleWsConnected = () => {
      // 仅在无活跃搜索/筛选时同步列表
      if (!isSearchActiveRef.current) {
        performSearch();
      }
    };
    const handleNewMessage = (event: Event) => {
      // 如果用户正在搜索/筛选，不扰乱搜索结果
      if (isSearchActiveRef.current) return;
      lastWsMessageTimeRef.current = Date.now(); // 记录消息时间，阻止备份同步立即覆盖
      const customEvent = event as CustomEvent;
      const newMessage = customEvent.detail as Message;
      if (!newMessage) return;
      const userId = (newMessage as any).user_id;
      setDisplayedUsers(prevUsers => {
        const idx = prevUsers.findIndex(u => u.id === userId);
        if (idx > -1) {
          const last = (prevUsers[idx] as any).lastMessage as Message | undefined;
          const isSame = last && (last.id === newMessage.id || (last.wa_id && (newMessage as any).wa_id && last.wa_id === (newMessage as any).wa_id));
          const updated = { ...prevUsers[idx], lastMessage: isSame ? last : newMessage };

          // 如果是新消息且是用户发的，增加未读数
          if (newMessage.sender === 'user' && !isSame && newMessage.status !== 'read') {
            updated.unreadCount = (updated.unreadCount || 0) + 1;
          }

          const next = [...prevUsers];
          next.splice(idx, 1);
          return [updated, ...next];
        }
        // 用户不在当前列表中（新用户或翻页外的用户），异步获取并插入
        if (userId) {
          searchUsersApi('', [], 0, 100).then(allUsers => {
            const found = allUsers.find((u: any) => u.id === userId);
            if (found) {
              setDisplayedUsers(prevUsers2 => {
                // 再次检查是否已存在（防止竞态条件）
                if (prevUsers2.some(u => u.id === userId)) return prevUsers2;
                const enriched = {
                  ...found,
                  lastMessage: newMessage,
                  unreadCount: newMessage.sender === 'user' ? 1 : 0,
                  tagsDetails: (found.tags?.map((tid: any) => allTagsRef.current.find(t => String(t.id) === String(typeof tid === 'string' ? tid : tid?.id)))?.filter((t): t is TagType => t != null) || []) as TagType[],
                } as User;
                return [enriched, ...prevUsers2];
              });
            }
          }).catch(() => {});
        }
        return prevUsers;
      });
    };

    const handleStatusUpdate = (event: Event) => {
      const customEvent = event as CustomEvent;
      const update = customEvent.detail as { message_id: string; status: Message['status']; user_id: string; wa_id?: string };
      if (!update) return;
      const { message_id, status, user_id } = update;
      let matched = false;
      setDisplayedUsers(prevUsers => prevUsers.map(u => {
        if (u.id !== user_id) return u;
        if (!u.lastMessage) return u;
        const matches = (u.lastMessage.id === message_id)
          || (!!u.lastMessage.wa_id && (u.lastMessage.wa_id === message_id || (update.wa_id && u.lastMessage.wa_id === update.wa_id)));
        if (!matches) return u;
        matched = true;
        return { ...u, lastMessage: { ...(u.lastMessage as any), status } as Message };
      }));
      if (!matched) {
        const now = Date.now();
        if (now - statusSyncRef.current > 2000) {
          statusSyncRef.current = now;
          // 使用 ref 避免过期闭包问题
          searchUsersApi(searchTermRef.current, activeTagsRef.current).then(users => {
            const enrichedUsers = users.map(u => ({
              ...u,
              tagsDetails: u.tags
                .map(tid => allTagsRef.current.find(t => t.id === (typeof tid === 'string' ? tid : (tid as any).id)))
                .filter(Boolean) as TagType[],
            }));
            setDisplayedUsers(enrichedUsers);
          }).catch(() => { });
        }
      }
    };

    window.addEventListener('new_message', handleNewMessage);
    window.addEventListener('message_status_update', handleStatusUpdate);
    window.addEventListener('ws_connected', handleWsConnected);
    const handleTagsUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      const detail = customEvent.detail as { user_id: string; tags: { id: number; name: string; color: string }[] };
      if (!detail) return;
      setDisplayedUsers(prev => prev.map(u => {
        if (u.id !== detail.user_id) return u;
        const tags = detail.tags.map(t => t.name);
        const tagsDetails = detail.tags.map(t => ({ id: String(t.id), name: t.name, color: t.color }));
        return { ...u, tags, tagsDetails } as any;
      }));
    };
    window.addEventListener('user_tags_updated', handleTagsUpdated);
    return () => {
      window.removeEventListener('new_message', handleNewMessage);
      window.removeEventListener('message_status_update', handleStatusUpdate);
      window.removeEventListener('ws_connected', handleWsConnected);
      window.removeEventListener('user_tags_updated', handleTagsUpdated);
    };
  }, []);

  const performSearch = useCallback(async (append: boolean = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsSearching(true);
      setHasMore(true);
    }
    try {
      const offset = append ? displayedUsers.length : 0;
      const users = await searchUsersApi(searchTerm, activeTags, offset, PAGE_SIZE);
      const enrichedUsers = users.map(u => ({
        ...u,
        tagsDetails: u.tags.map(tid => allTagsRef.current.find(t => String(t.id) === String(typeof tid === 'string' ? tid : (tid as any).id))).filter(Boolean) as TagType[]
      }));

      if (append) {
        setDisplayedUsers((prev: User[]) => [...prev, ...enrichedUsers]);
      } else {
        setDisplayedUsers(enrichedUsers);
      }

      // 如果返回的用户数少于PAGE_SIZE，说明没有更多了
      if (users.length < PAGE_SIZE) {
        setHasMore(false);
      }
    } catch (error) {
      console.error("[UserList] Failed to search users:", error);
      if (!append) {
        setDisplayedUsers([]);
      }
    } finally {
      if (append) {
        setIsLoadingMore(false);
      } else {
        setIsSearching(false);
      }
    }
  }, [searchTerm, activeTags, displayedUsers.length]);

  // 加载更多用户
  const loadMore = useCallback(() => {
    if (!isLoadingMore && hasMore && !isSearching) {
      performSearch(true);
    }
  }, [isLoadingMore, hasMore, isSearching, performSearch]);

  // 无限滚动监听
  useEffect(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      // 当滚动到距底部100px时加载更多
      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loadMore]);

  // 备用同步机制：每30秒检查一次状态更新（仅在无搜索条件时）
  useEffect(() => {
    // 清除之前的定时器
    if (backupSyncRef.current) {
      clearInterval(backupSyncRef.current);
      backupSyncRef.current = undefined;
    }

    // 使用 ref 检查，避免闭包过期问题
    if (!isSearchActiveRef.current) {
      backupSyncRef.current = setInterval(() => {
        // 执行时再次检查，确保搜索结果不会被覆盖
        if (isSearchActiveRef.current) return;
        // 如果最近10秒内有 WebSocket 消息更新，跳过本次同步
        if (Date.now() - lastWsMessageTimeRef.current < 10000) return;
        searchUsersApi('', []).then(users => {
          // API 返回后再次确认用户仍不在搜索状态
          if (isSearchActiveRef.current) return;
          const enrichedUsers = users.map(u => ({
            ...u,
            tagsDetails: u.tags
              .map((tid: any) => allTagsRef.current.find(t => String(t.id) === String(typeof tid === 'string' ? tid : (tid as any).id)))
              .filter(Boolean) as TagType[],
          }));
          setDisplayedUsers(enrichedUsers);
        }).catch(() => { });
      }, 30000);
    }

    return () => {
      if (backupSyncRef.current) {
        clearInterval(backupSyncRef.current);
        backupSyncRef.current = undefined;
      }
    };
  }, [searchTerm, activeTags.length]);

  useEffect(() => {
    const handler = setTimeout(() => {
      performSearch(false);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm, activeTags]); // 移除 allTags 依赖，使用 ref 避免意外重新搜索

  const toggleTag = (tagId: string) => {
    setActiveTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  return (
    <aside className="w-full md:w-80 lg:w-96 bg-card md:border-r border-border flex flex-col h-full flex-shrink-0 overflow-hidden">
      <div className="p-4 border-b border-border space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            placeholder="搜索用户、电话或关键词..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 bg-input border border-border rounded-md focus:ring-2 focus:ring-ring focus:outline-none"
          />
          {/* 搜索中显示loading，有内容时显示清除按钮 */}
          {isSearching ? (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-primary animate-spin" />
          ) : searchTerm ? (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="清除搜索"
            >
              <XCircle className="w-5 h-5" />
            </button>
          ) : null}
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <TagFilterDropdown tags={allTags} activeTags={activeTags} toggleTag={toggleTag} />
          </div>
          <div className="flex-1">
            <button
              onClick={() => setIsTagManagerOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-input border border-border rounded-md text-sm hover:bg-muted"
            >
              <Tag className="w-4 h-4 text-muted-foreground" />
              <span>标签管理</span>
            </button>
          </div>
        </div>
        {/* 物流查询按钮 */}
        <button
          onClick={() => setIsTrackingOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary/10 border border-primary/30 rounded-md text-sm hover:bg-primary/20 text-primary"
        >
          <Truck className="w-4 h-4" />
          <span>物流查询</span>
        </button>
        {/* 日志追踪按钮 */}
        <button
          onClick={() => setIsAnalysisLogOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-orange-500/10 border border-orange-500/30 rounded-md text-sm hover:bg-orange-500/20 text-orange-600"
        >
          <FileText className="w-4 h-4" />
          <span>订单分析日志</span>
        </button>
        {/* 模型配置按钮 */}
        <button
          onClick={() => setIsModelConfigOpen(true)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/10 border border-purple-500/30 rounded-md text-sm hover:bg-purple-500/20 text-purple-400"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M9 9h6v6H9z"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M20 15h2"/><path d="M9 2v2"/><path d="M9 20v2"/><path d="M2 9h2"/><path d="M20 9h2"/></svg>
          <span>模型配置</span>
        </button>
      </div>
      {isTagManagerOpen && (
        <TagManagerModal
          onClose={() => setIsTagManagerOpen(false)}
        />
      )}
      {isTrackingOpen && (
        <TrackingModal
          onClose={() => setIsTrackingOpen(false)}
        />
      )}
      {isAnalysisLogOpen && (
        <AnalysisLogModal
          onClose={() => setIsAnalysisLogOpen(false)}
        />
      )}
      {isModelConfigOpen && (
        <ModelConfigModal
          onClose={() => setIsModelConfigOpen(false)}
        />
      )}
      <div ref={listContainerRef} className="flex-1 overflow-y-auto">
        {isSearching ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : displayedUsers.length > 0 ? (
          <>
            <ul>
              {displayedUsers.map((user) => (
                <UserItem
                  key={user.id}
                  user={user as any}
                  isSelected={user.id === selectedUserId}
                  onSelect={() => onSelectUser(user)}
                />
              ))}
            </ul>
            {isLoadingMore && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
                <span className="ml-2 text-sm text-muted-foreground">加载更多...</span>
              </div>
            )}
            {!hasMore && displayedUsers.length >= PAGE_SIZE && (
              <div className="text-center py-4 text-sm text-muted-foreground">
                没有更多用户了
              </div>
            )}
          </>
        ) : (
          <div className="text-center p-8 text-muted-foreground h-full flex flex-col justify-center items-center">
            <XCircle className="w-12 h-12 mb-4 text-muted-foreground/50" />
            <h3 className="font-semibold text-lg text-foreground">未找到用户</h3>
            <p className="text-sm">请尝试调整您的搜索或筛选条件。</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default UserList;
